/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

import {computed, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';

export const YEARLY_REPORT_SCOPE_MANAGER = 'manager';
export const YEARLY_REPORT_SCOPE_ADMIN = 'admin';

export type YearlyReportScope =
  | typeof YEARLY_REPORT_SCOPE_MANAGER
  | typeof YEARLY_REPORT_SCOPE_ADMIN;

export type MonthStats = {
  year: number;
  month: number;
  unrespectedRestDays: number;
  workedDays: number;
};

export type EmployeeMonthlyStats = {
  empNumber: number;
  firstName: string;
  lastName: string;
  fullName: string;
  fromDate: string;
  toDate: string;
  months: MonthStats[];
  totalUnrespectedRestDays: number;
  totalWorkedDays: number;
};

export type MonthlyReportState = {
  stats: EmployeeMonthlyStats | null;
  isLoading: boolean;
  error: string | null;
};

export type UseYearlyReportOptions = {
  scope: YearlyReportScope;
};

export const MONTH_SHORT_NAMES_FR: readonly string[] = [
  'Janv.',
  'Févr.',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juil.',
  'Août',
  'Sept.',
  'Oct.',
  'Nov.',
  'Déc.',
];

export function buildYearlyReportUrl(): string {
  return '/api/v2/forfait-jours/yearly-reports';
}

export default function useYearlyReport(options: UseYearlyReportOptions) {
  const http = new APIService(
    // @ts-expect-error: appGlobal is not in window object by default
    window.appGlobal.baseUrl,
    buildYearlyReportUrl(),
  );

  const empNumber = ref<number | null>(null);
  const fromDate = ref<string | null>(null);
  const toDate = ref<string | null>(null);
  const stats = ref<EmployeeMonthlyStats | null>(null);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const includePastEmployees = ref<boolean>(false);

  const leavePeriodStartDate = ref<string | null>(null);
  const leavePeriodEndDate = ref<string | null>(null);
  const leavePeriodDefined = ref<boolean>(false);
  const isLoadingPeriod = ref<boolean>(false);

  const isAdmin = computed<boolean>(
    () => options.scope === YEARLY_REPORT_SCOPE_ADMIN,
  );

  /**
   * Fetch the current leave period to set default from/to dates.
   * Pattern follows the admin LeaveListTable (LEAVE-01): GET
   * /api/v2/leave/leave-periods, read meta.currentLeavePeriod.
   */
  async function loadLeavePeriod(): Promise<void> {
    isLoadingPeriod.value = true;
    try {
      const periodHttp = new APIService(
        // @ts-expect-error: appGlobal not in window
        window.appGlobal.baseUrl,
        '/api/v2/leave/leave-periods',
      );
      const response = await periodHttp.request({method: 'GET'});
      const meta = response?.data?.meta;
      if (meta?.leavePeriodDefined && meta?.currentLeavePeriod) {
        leavePeriodDefined.value = true;
        leavePeriodStartDate.value = meta.currentLeavePeriod.startDate;
        leavePeriodEndDate.value = meta.currentLeavePeriod.endDate;
        // Default dates from the current leave period.
        if (!fromDate.value) {
          fromDate.value = meta.currentLeavePeriod.startDate;
        }
        if (!toDate.value) {
          toDate.value = meta.currentLeavePeriod.endDate;
        }
      } else if (Array.isArray(response?.data?.data) && response.data.data[0]) {
        // Fallback when leave period is not defined.
        const first = response.data.data[0];
        leavePeriodStartDate.value = first.startDate;
        leavePeriodEndDate.value = first.endDate;
        if (!fromDate.value) {
          fromDate.value = first.startDate;
        }
        if (!toDate.value) {
          toDate.value = first.endDate;
        }
      }
    } catch {
      // Period fetch failure is not fatal — user can still search.
    } finally {
      isLoadingPeriod.value = false;
    }
  }

  async function load(): Promise<void> {
    if (!empNumber.value || !fromDate.value || !toDate.value) {
      return;
    }

    isLoading.value = true;
    error.value = null;
    stats.value = null;

    try {
      const params: Record<string, string> = {
        scope: options.scope,
        empNumber: String(empNumber.value),
        fromDate: fromDate.value,
        toDate: toDate.value,
        includePastEmployees: String(includePastEmployees.value),
      };
      const response = await http.request({method: 'GET', params});
      if (response?.status >= 400) {
        error.value = `Server returned ${response.status}`;
        return;
      }
      const payload = response?.data?.data;
      if (!payload || typeof payload !== 'object') {
        throw new Error('Malformed response from server.');
      }
      stats.value = payload as unknown as EmployeeMonthlyStats;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      error.value = message;
    } finally {
      isLoading.value = false;
    }
  }

  function reset(): void {
    empNumber.value = null;
    fromDate.value = leavePeriodStartDate.value;
    toDate.value = leavePeriodEndDate.value;
    stats.value = null;
    error.value = null;
    includePastEmployees.value = false;
  }

  /**
   * Format a month index (1–12) to short French name.
   */
  function monthLabel(year: number, month: number): string {
    return `${MONTH_SHORT_NAMES_FR[month - 1]} ${year}`;
  }

  return {
    empNumber,
    fromDate,
    toDate,
    stats,
    isLoading,
    error,
    isAdmin,
    includePastEmployees,
    leavePeriodStartDate,
    leavePeriodEndDate,
    leavePeriodDefined,
    isLoadingPeriod,
    loadLeavePeriod,
    load,
    reset,
    monthLabel,
  };
}
